import { UzCombobox } from '@utazzo/ui';
import React from 'react';

import { usePickerOptions } from './usePickerOptions';

import * as fdApi from '@/features/fixed-departures/fixed-departures.api';

interface FixedDeparturePickerProps {
  label?: string;
  value: string | null;
  onValueChange: (value: string | null) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  hint?: string;
  placeholder?: string;
}

/**
 * Searchable picker over ACTIVE fixed-departure templates. Used to switch an
 * in-progress FD itinerary onto a different template (ADR-0069): the operator
 * searches any FD by title or reference and re-seeds the whole trip from it.
 */
export const FixedDeparturePicker = ({
  label = 'Fixed departure',
  value,
  onValueChange,
  required,
  disabled,
  error,
  hint,
  placeholder = 'Search a fixed departure',
}: FixedDeparturePickerProps): React.JSX.Element => {
  const { options, loading, setSearch } = usePickerOptions(
    {
      cacheKey: 'picker.fixed-departures',
      fetch: async (search) => {
        const res = await fdApi.listFixedDepartures({
          page: 1,
          pageSize: 30,
          sortBy: 'updatedAt',
          sortDir: 'desc',
          // Only sellable templates can be switched to (mirrors the picker).
          sellableOnly: true,
          ...(search.trim().length >= 3 ? { q: search.trim() } : {}),
        });
        return res.data;
      },
      fetchOne: async (id) => {
        try {
          return await fdApi.getFixedDeparture(id);
        } catch {
          return null;
        }
      },
      toOption: (fd) => ({
        value: fd.id,
        label: fd.title,
        description: fd.reference,
      }),
    },
    value,
  );

  return (
    <UzCombobox
      label={label}
      placeholder={placeholder}
      searchPlaceholder="Search by title or reference..."
      value={value ?? ''}
      onValueChange={(v) => onValueChange(v || null)}
      onSearchChange={setSearch}
      options={options}
      loading={loading}
      required={required}
      disabled={disabled}
      error={error}
      hint={hint}
    />
  );
};
